import { Box, Tabs } from '@wix/design-system';
import React, { useContext, useMemo } from 'react';

import { RicosContext } from './../../ricos-context';
import type { LogBiFn } from './../../_shared/bi-context-provider/bi-context-provider';
import type { PmActionButtonNode, PmLinkButtonNode, PmNodeType } from './../../_shared/pm-node-types';
import { dataHook } from './../../wix-rich-content-common';
import { unreachable } from './../../editor-common';
import { Panel, PanelHeader } from './../../ui-components';

import type { StylesData } from '../components/design-component';
import { DesignComponent } from '../components/design-component';
import { SettingsComponent } from '../components/settings-component';
import type { SettingsTab } from '../constants';
import { SETTINGS_TAB } from '../constants';
import {
  ROOT,
  SETTINGS_DESIGN_TAB,
  SETTINGS_GENERAL_TAB,
  SETTINGS_MODAL,
  SETTINGS_TABS,
} from '../data-hooks';
import classes from '../statics/styles/button-input-modal-scss';
import type { ButtonPluginEditorConfigCommon } from '../types';

type Props = {
  buttonData: (PmLinkButtonNode | PmActionButtonNode)['attrs'];
  config: ButtonPluginEditorConfigCommon;
  onClose: () => void;
  onChange: (data: Partial<(PmLinkButtonNode | PmActionButtonNode)['attrs']>) => void;
  nodeId: string;
  nodeType: typeof PmNodeType.LINK_BUTTON | typeof PmNodeType.ACTION_BUTTON;
  activeTab: SettingsTab;
  onTabChange: (tab: SettingsTab) => void;
  logBi: LogBiFn;
};

type RecursivePartial<T> = {
  [P in keyof T]?: RecursivePartial<T[P]>;
};

const defaultBorderRadius = 0;
const defaultBorderWidth = 0;

export const SettingsModalContent: React.FC<Props> = ({
  buttonData,
  onChange,
  config,
  nodeId,
  nodeType,
  activeTab,
  onTabChange,
  onClose,
  logBi,
}) => {
  const { t, isMobile } = useContext(RicosContext);

  const { defaultBackgroundColor, defaultTextColor } = useMemo(() => {
    const button = document.querySelector(dataHook(`editor-${nodeId}`, ROOT));
    const style = button ? window.getComputedStyle(button) : undefined;
    // var name and fallback taken from statics/styles/default-styles.scss
    const defaultBackgroundColor =
      config.colors?.color8 ?? (style?.getPropertyValue('--ricos-action-color') || '#116DFF');
    const defaultTextColor =
      config.colors?.color1 ??
      (style?.getPropertyValue('--ricos-text-on-action-color') ||
        style?.getPropertyValue('--ricos-background-color') ||
        '#FFFFFF');
    return { defaultBackgroundColor, defaultTextColor };
  }, []);

  const resolveButtonStyles = (
    componentData: Partial<(PmLinkButtonNode | PmActionButtonNode)['attrs']['styles']>
  ): StylesData => {
    return {
      border: {
        width: componentData?.border?.width ?? defaultBorderWidth,
        radius: componentData?.border?.radius ?? defaultBorderRadius,
      },
      colors: {
        text: componentData?.colors?.text,
        border: componentData?.colors?.border,
        background: componentData?.colors?.background,
      },
    };
  };

  const resolveStylesChange = ({ colors, border }: StylesData): RecursivePartial<StylesData> => {
    return {
      colors,
      border: {
        width: border.width === defaultBorderWidth ? undefined : border.width,
        radius: border.radius === defaultBorderRadius ? undefined : border.radius,
      },
    };
  };

  return (
    <div className={classes.modal}>
      <Panel onCloseButtonClick={onClose} dataHook={SETTINGS_MODAL}>
        <PanelHeader title={t('ButtonModal_Header')} />
        <Tabs
          dataHook={SETTINGS_TABS}
          hasDivider
          onClick={tab => onTabChange(tab.id as SettingsTab)}
          activeId={activeTab}
          className={classes.tabs}
          items={[
            {
              id: SETTINGS_TAB.SETTINGS,
              title: t('ButtonModal_Settings_Tab'),
              dataHook: SETTINGS_GENERAL_TAB,
            },
            {
              id: SETTINGS_TAB.DESIGN,
              title: t('ButtonModal_Design_Tab'),
              dataHook: SETTINGS_DESIGN_TAB,
            },
          ]}
        />
        <Box className={isMobile ? undefined : classes.content}>
          {(() => {
            switch (activeTab) {
              case SETTINGS_TAB.SETTINGS:
                return (
                  <SettingsComponent buttonData={buttonData} onChange={onChange} logBi={logBi} />
                );
              case SETTINGS_TAB.DESIGN:
                return (
                  <DesignComponent
                    stylesData={resolveButtonStyles(buttonData.styles)}
                    onChange={newData => {
                      const styles = resolveStylesChange(newData);
                      onChange({ styles });
                    }}
                    config={config}
                    defaultBackgroundColor={defaultBackgroundColor}
                    defaultTextColor={defaultTextColor}
                    nodeId={nodeId}
                    nodeType={nodeType}
                    logBi={logBi}
                  />
                );
              default:
                unreachable(activeTab);
                return null;
            }
          })()}
        </Box>
      </Panel>
    </div>
  );
};
